import { PaliersEditor } from '@/components/paliers-editor';
import { ApiError, matricesApi } from '@/lib/api';
import { useDebouncedCallback } from '@/lib/use-debounced-callback';
import {
  type CompanyWithSetup,
  MATRIX_PIVOT_PLURAL_LABELS,
  type MatrixPivot,
  type MatrixWithDimensions,
  type ResolvedPalier,
} from '@mss_creator/shared';
import { Alert, Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { DerivedFromBanner } from './derived-from-banner';
import { PivotSelector } from './pivot-selector';
import { SetupStepRow } from './setup-step-row';

/** Same delay as the sujet/contexte autosave. */
const AUTOSAVE_DEBOUNCE_MS = 500;

interface SetupReviewStepProps {
  matrix: MatrixWithDimensions;
  company: CompanyWithSetup;
  /** The pivot on screen, owned by the wizard so the later steps read the same value. */
  pivot: MatrixPivot;
  onPivotChange: (pivot: MatrixPivot) => void;
  /** `derivedFrom` search param: the matrix this draft was created from. */
  derivedFrom?: string | undefined;
  /** `pivotReset` search param: the source's pivot fell back to Levier de valeur. */
  pivotReset?: boolean | undefined;
  /** The company is archived (issue #22): the pivot and the paliers are shown, not editable. */
  readOnly?: boolean;
  onContinue: () => void;
}

function pivotErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'PIVOT_NOT_FILLED') {
    return "Ce pivot n'est plus renseigné pour toutes les étapes du setup de l'entreprise : le pivot enregistré a été rétabli.";
  }
  if (error instanceof ApiError && error.code === 'MATRIX_PIVOT_FROZEN') {
    return 'Le pivot est figé depuis la première génération : le pivot enregistré a été rétabli.';
  }
  return "Le pivot n'a pas pu être enregistré : le pivot enregistré a été rétabli. Réessayez.";
}

/**
 * Step 1 — the company's setup, read through the matrix's pivot, and the
 * matrix's own paliers (issue #16).
 *
 * The setup itself stays read-only here: it belongs to the company sheet. What
 * this step writes belongs to the matrix alone — its pivot (saved on each
 * change) and its palier overrides (`matrix_paliers`, autosaved after a short
 * delay like the sujet and contexte). Neither can reach the company sheet.
 *
 * A pivot change also clears any stored `initialPromptOverride`: that text is
 * the récapitulatif's prompt with the previous pivot already resolved in it,
 * so keeping it would make the generation use a pivot other than the one
 * saved. The récapitulatif then recomposes its prompt on the next visit, or
 * asks before discarding an edit still on screen, exactly as it does for a
 * palier selection change.
 */
export function SetupReviewStep({
  matrix,
  company,
  pivot,
  onPivotChange,
  derivedFrom,
  pivotReset,
  readOnly = false,
  onContinue,
}: SetupReviewStepProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [savedPivot, setSavedPivot] = useState<MatrixPivot>(matrix.pivot);
  const [pivotError, setPivotError] = useState<string | null>(null);

  // « Avant la première génération » reads on `paliersFrozenAt` (règle
  // complémentaire 1): a draft dropped back by a failed generation keeps its
  // pivot frozen, and the backend would refuse it 409.
  const frozen = matrix.paliersFrozenAt !== null;

  const pivotMutation = useMutation({
    mutationFn: (next: MatrixPivot) =>
      matricesApi.patch(matrix.id, { pivot: next, initialPromptOverride: null }),
    onSuccess: (updated) => {
      setSavedPivot(updated.pivot);
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrix.id] });
    },
    onError: (error) => {
      onPivotChange(savedPivot);
      setPivotError(pivotErrorMessage(error));
      // The setup may have changed since the page loaded (the 422 case): a
      // fresh copy disables the option that no longer qualifies.
      void queryClient.invalidateQueries({ queryKey: ['company', company.id] });
    },
  });

  function handlePivotChange(next: MatrixPivot) {
    setPivotError(null);
    onPivotChange(next);
    pivotMutation.mutate(next);
  }

  // Same levels as the Cadrage tab (see `PaliersSection`): a draft inherits
  // from its company, a frozen one from the global defaults.
  const inherited = useMemo<ResolvedPalier[]>(
    () =>
      frozen
        ? company.palierDefaults.map((fallback) => ({
            number: fallback.number,
            label: fallback.defaultLabel,
            description: fallback.defaultDescription,
          }))
        : company.paliers,
    [frozen, company.palierDefaults, company.paliers],
  );
  const [paliers, setPaliers] = useState<ResolvedPalier[]>(matrix.paliers);

  const paliersMutation = useMutation({
    mutationFn: (draft: ResolvedPalier[]) =>
      matricesApi.updatePaliers(
        matrix.id,
        draft.map((palier) => {
          const base = inherited.find((entry) => entry.number === palier.number);
          // A value equal to the inherited one persists as `null`: that is
          // what « Reprendre la valeur de l'entreprise » means.
          return {
            number: palier.number,
            customLabel: palier.label === base?.label ? null : palier.label,
            customDescription:
              (palier.description ?? null) === (base?.description ?? null)
                ? null
                : palier.description,
          };
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrix.id] });
    },
  });
  const debouncedSavePaliers = useDebouncedCallback((draft: ResolvedPalier[]) => {
    paliersMutation.mutate(draft);
  }, AUTOSAVE_DEBOUNCE_MS);

  function handlePaliersChange(next: ResolvedPalier[]) {
    setPaliers(next);
    debouncedSavePaliers(next);
  }

  function handleContinue() {
    debouncedSavePaliers.flush();
    onContinue();
  }

  function handleExpandedChange(order: number, next: boolean) {
    setExpanded((previous) => {
      const updated = new Set(previous);
      if (next) updated.add(order);
      else updated.delete(order);
      return updated;
    });
  }

  return (
    <section className="space-y-6">
      <DerivedFromBanner sourceMatrixId={derivedFrom} pivotReset={pivotReset} />

      <div>
        <h2 className="font-semibold text-h3">Setup de singularité — {company.name}</h2>
        <p className="text-gray-600 text-small">
          Choisissez le pivot de la matrice et ajustez ses paliers. Le setup lui-même se modifie sur
          la fiche entreprise.
        </p>
      </div>

      <div className="space-y-2">
        <PivotSelector
          value={pivot}
          setup={company.singularitySetup}
          companyId={company.id}
          readOnly={frozen || readOnly}
          archived={readOnly}
          onChange={handlePivotChange}
        />
        {pivotError && <Alert tone="danger">{pivotError}</Alert>}
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold text-body-lg">{MATRIX_PIVOT_PLURAL_LABELS[pivot]}</h3>
        <div className="space-y-2">
          {company.singularitySetup.map((step) => (
            <SetupStepRow
              key={step.order}
              pivot={pivot}
              step={step}
              expanded={expanded.has(step.order)}
              onExpandedChange={(next) => handleExpandedChange(step.order, next)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="font-semibold text-body-lg">Paliers</h3>
          <span className="text-gray-600 text-small">
            {frozen
              ? 'Propres à cette matrice, figés à la première génération.'
              : `Propres à cette matrice, hérités de ${company.name}.`}
          </span>
          <span className="flex-1" />
          {paliersMutation.isPending && (
            <output className="text-gray-500 text-small">Enregistrement…</output>
          )}
          {!paliersMutation.isPending && paliersMutation.isSuccess && (
            <output className="inline-flex items-center gap-1 text-gray-500 text-small">
              <Icon name="check" size={16} />
              Enregistré
            </output>
          )}
        </div>
        <PaliersEditor
          paliers={paliers}
          inherited={inherited}
          resetLabel={
            frozen ? 'Rétablir la valeur par défaut' : "Reprendre la valeur de l'entreprise"
          }
          overriddenLegend="personnalisé pour cette matrice"
          readOnly={readOnly}
          onChange={handlePaliersChange}
        />
        {paliersMutation.isError && (
          <Alert tone="danger">
            Les paliers n'ont pas pu être enregistrés. Modifiez-les à nouveau pour réessayer.
          </Alert>
        )}
      </div>

      <div className="sticky bottom-0 flex items-center justify-end gap-4 bg-white pt-4">
        <span className="text-gray-500 text-small">
          {readOnly
            ? 'Entreprise archivée : les modifications sont désactivées.'
            : 'Les modifications sont enregistrées automatiquement.'}
        </span>
        <Button variant="secondary" type="button" onClick={handleContinue}>
          Continuer
        </Button>
      </div>
    </section>
  );
}
